import { AuthService } from "../utils/auth";
import { fetchWithCache, invalidateCacheByPrefix } from "../utils/requestCache";

const API_BASE_URL =
  process.env.REACT_APP_API_URL || "http://localhost:3000/api/v1";

const PLANS_CACHE_DURATION = 5 * 60 * 1000; // 5 minutes
const CREDIT_PACKS_CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

// ============ Types ============

export interface SubscriptionPlan {
  id: string;
  name: string;
  description: string;
  price?: number;
  monthlyPrice: string | number;
  billingCycle?: "monthly" | "quarterly" | "yearly";
  creditsPerCycle?: number;
  creditsPerMonth?: number;
  discountPercentage?: number | null;
  displayOrder?: number;
  features?: string[];
  isActive: boolean;
  sortOrder?: number;
  serviceId?: string;
  service?: {
    id: string;
    name: string;
    description?: string;
    price: string | number;
    features: string[];
    category: string;
    durationText?: string;
    requirements?: string[];
    limitations?: string[];
    includesPickup?: boolean;
  };
  stripePriceId?: string;
  stripeProductId?: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface UserSubscription {
  id: string;
  userId: string;
  planId: string;
  status: "active" | "paused" | "cancelled" | "past_due" | "incomplete";
  stripeSubscriptionId?: string;
  currentPeriodStart: string;
  currentPeriodEnd: string;
  cancelAtPeriodEnd: boolean;
  cancelledAt?: string;
  plan?: SubscriptionPlan;
  createdAt: string;
  updatedAt: string;
}

export interface UserCredit {
  id: string;
  userId: string;
  serviceId: string;
  balance: number;
  service?: {
    id: string;
    name: string;
    category: string;
  };
}

export interface CreditBalance {
  totalCredits: number;
  usedCredits: number;
  availableCredits: number;
  expiringCredits: number;
  expiringDate: string | null;
}

export interface CreditTransaction {
  id: string;
  userId: string;
  serviceId: string;
  amount: number;
  type: "grant" | "use" | "expire" | "refund";
  description?: string;
  orderId?: string;
  subscriptionId?: string;
  createdAt: string;
}

export interface CheckoutSession {
  sessionId: string;
  url: string;
}

interface ApiResponse<T> {
  success: boolean;
  message: string;
  data?: T;
  error?: string;
}

// ============ API Service ============

class SubscriptionApiService {
  private async request<T>(
    endpoint: string,
    options?: RequestInit,
    requireAuth = false,
  ): Promise<T> {
    const url = `${API_BASE_URL}${endpoint}`;
    const headers: Record<string, string> = {
      "Content-Type": "application/json",
    };

    if (requireAuth) {
      const token = AuthService.getAccessToken();
      if (!token) {
        throw new Error("Authentication required");
      }
      headers["Authorization"] = `Bearer ${token}`;
    }

    try {
      const response = await fetch(url, {
        headers: {
          ...headers,
          ...options?.headers,
        },
        ...options,
      });

      const data: ApiResponse<T> = await response.json();

      if (!response.ok || !data.success) {
        throw new Error(data.error || data.message || "Request failed");
      }

      return data.data as T;
    } catch (error) {
      console.error(`Subscription API request failed for ${endpoint}:`, error);
      throw error;
    }
  }

  // ============ Public Endpoints ============

  /**
   * Get all active subscription plans (public) - cached
   * @param lang - Language code (fr, en) - defaults to 'fr'
   */
  async getPlans(lang: string = "fr"): Promise<SubscriptionPlan[]> {
    const cacheKey = `subscription_plans_${lang}`;

    return fetchWithCache(
      cacheKey,
      async () => {
        const result = await this.request<{ plans: SubscriptionPlan[] }>(
          `/subscriptions/plans?lang=${lang}`,
        );
        return result.plans;
      },
      PLANS_CACHE_DURATION,
    );
  }

  /**
   * Get all credit packs (public) - cached
   */
  async getCreditPacks(): Promise<any[]> {
    const cacheKey = "credit_packs";

    return fetchWithCache(
      cacheKey,
      async () => {
        const result = await this.request<{ creditPacks: any[] }>(
          `/credit-packs`,
        );
        return result.creditPacks;
      },
      CREDIT_PACKS_CACHE_DURATION,
    );
  }

  /**
   * Invalidate subscription-related caches
   */
  invalidateCache(): void {
    invalidateCacheByPrefix("subscription_");
    invalidateCacheByPrefix("credit_packs");
  }

  /**
   * Get a specific subscription plan by ID (public)
   */
  async getPlanById(planId: string): Promise<SubscriptionPlan> {
    const result = await this.request<{ plan: SubscriptionPlan }>(
      `/subscriptions/plans/${planId}`,
    );
    return result.plan;
  }

  // ============ Authenticated Endpoints ============

  /**
   * Get current user's active subscription
   */
  async getMySubscription(): Promise<UserSubscription | null> {
    const result = await this.request<{
      subscription: UserSubscription | null;
    }>("/subscriptions/my", undefined, true);
    return result.subscription;
  }

  /**
   * Get user's subscription history
   */
  async getSubscriptionHistory(): Promise<UserSubscription[]> {
    const result = await this.request<{ subscriptions: UserSubscription[] }>(
      "/subscriptions/history",
      undefined,
      true,
    );
    return result.subscriptions;
  }

  /**
   * Create a checkout session for subscription
   */
  async createCheckoutSession(
    planId: string,
    language: string = "fr",
  ): Promise<CheckoutSession> {
    return this.request<CheckoutSession>(
      "/subscriptions/checkout",
      {
        method: "POST",
        body: JSON.stringify({ planId, language }),
      },
      true,
    );
  }

  /**
   * Cancel current subscription
   */
  async cancelSubscription(immediately = false): Promise<UserSubscription> {
    const result = await this.request<{ subscription: UserSubscription }>(
      "/subscriptions/cancel",
      {
        method: "POST",
        body: JSON.stringify({ immediately }),
      },
      true,
    );
    return result.subscription;
  }

  /**
   * Resume a subscription that was set to cancel at period end
   */
  async resumeSubscription(): Promise<UserSubscription> {
    const result = await this.request<{ subscription: UserSubscription }>(
      "/subscriptions/resume",
      {
        method: "POST",
      },
      true,
    );
    return result.subscription;
  }

  // ============ Credits Endpoints ============

  /**
   * Get user's total credit balance
   */
  async getCreditBalance(): Promise<CreditBalance> {
    const result = await this.request<{ balance: CreditBalance }>(
      "/subscriptions/credits/balance",
      undefined,
      true,
    );
    return result.balance;
  }

  /**
   * Get user's credits grouped by service
   */
  async getCreditsByService(): Promise<UserCredit[]> {
    const result = await this.request<{ creditsByService: UserCredit[] }>(
      "/subscriptions/credits/by-service",
      undefined,
      true,
    );
    return result.creditsByService;
  }

  /**
   * Get user's credit transaction history
   */
  async getCreditHistory(params?: {
    serviceId?: string;
    type?: string;
    page?: number;
    limit?: number;
  }): Promise<{ transactions: CreditTransaction[]; pagination: any }> {
    const searchParams = new URLSearchParams();
    if (params?.serviceId) searchParams.append("serviceId", params.serviceId);
    if (params?.type) searchParams.append("type", params.type);
    // Backend uses offset, not page - convert page to offset
    const limit = params?.limit || 10;
    if (params?.page) {
      const offset = (params.page - 1) * limit;
      searchParams.append("offset", String(offset));
    }
    searchParams.append("limit", String(limit));

    const queryString = searchParams.toString();
    const endpoint = `/subscriptions/credits/history${
      queryString ? `?${queryString}` : ""
    }`;

    const response = await this.request<{
      transactions: CreditTransaction[];
      total: number;
      limit: number;
      offset: number;
    }>(endpoint, undefined, true);

    // Convert backend response to pagination format expected by frontend
    const totalPages = Math.ceil((response.total || 0) / limit);
    return {
      transactions: response.transactions,
      pagination: {
        total: response.total,
        totalPages,
        page: params?.page || 1,
        limit,
      },
    };
  }

  // ============ Credit Booking Endpoints ============

  /**
   * Calculate credit usage for a potential order
   */
  async calculateCreditUsage(
    serviceId: string,
    numberOfPairs: number,
  ): Promise<{
    creditsAvailable: number;
    creditsToUse: number;
    creditValue: number;
    upgradeAmount: number;
    totalWithCredits: number;
  }> {
    return this.request(
      "/subscriptions/credits/calculate",
      {
        method: "POST",
        body: JSON.stringify({ serviceId, numberOfPairs }),
      },
      true,
    );
  }

  /**
   * Create an order using credits (subscription booking)
   */
  async bookWithCredits(data: {
    serviceId: string;
    numberOfPairs: number;
    pickupMethod: "pickup" | "relay";
    pickupAddress?: string;
    pickupCity?: string;
    pickupPostalCode?: string;
    relaySpotId?: string;
    pickupDate?: string;
    pickupTimeSlot?: string;
    specialInstructions?: string;
    wantsPickupUpgrade?: boolean;
    photoDraftId?: string;
  }): Promise<{
    order: any;
    creditsUsed: number;
    supplementRequired: boolean;
    supplementAmount?: number;
    supplementReason?: string;
  }> {
    return this.request(
      "/subscriptions/credits/book",
      {
        method: "POST",
        body: JSON.stringify(data),
      },
      true,
    );
  }

  /**
   * Create a checkout session for supplement payment
   */
  async createSupplementCheckout(
    orderId: string,
    amount: number,
    reason: string,
  ): Promise<{ sessionId: string; url: string }> {
    return this.request(
      "/subscriptions/credits/book/supplement",
      {
        method: "POST",
        body: JSON.stringify({ orderId, amount, reason }),
      },
      true,
    );
  }
}

export const subscriptionApi = new SubscriptionApiService();
export default subscriptionApi;
